import { useEffect, useState } from "react";
import { Check, Copy, Link2, Share2, Users } from "lucide-react";

import { api } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import Loader from "@/components/ui/loader";
import { Pagination } from "@/components/ui/pagination";
import {
  EmptyPanel,
  PageHero,
  PagePanel,
  PageShell,
  PanelHeading,
} from "@/components/interior-page";

const PAGE_SIZE = 15;

interface ReferredBuyer {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  status: string;
  created_at: string;
}

const STATUS_COLORS: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-700",
  inactive: "bg-slate-100 text-slate-600",
  pending_verification: "bg-amber-100 text-amber-700",
};

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  inactive: "Inactive",
  pending_verification: "Pending",
};

export default function BLAdvisorReferralsPage() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [buyers, setBuyers] = useState<ReferredBuyer[]>([]);
  const [buyersTotal, setBuyersTotal] = useState(0);
  const [buyersLoading, setBuyersLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [copied, setCopied] = useState(false);

  const appUrl = (import.meta.env.VITE_PUBLIC_APP_URL || window.location.origin).replace(/\/$/, "");
  const referralLink = code ? `${appUrl}/register?ref=${code}` : "";

  // Load the advisor's code + headline total once.
  useEffect(() => {
    let cancelled = false;
    api
      .get<{ code: string; total_buyers: number }>("/referrals/me")
      .then((me) => {
        if (cancelled) return;
        setCode(me.code);
        setTotal(me.total_buyers);
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load your referral details");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [toast]);

  // Load the current page of referred buyers.
  useEffect(() => {
    let cancelled = false;
    setBuyersLoading(true);
    api
      .get<{ total_elements: number; data: ReferredBuyer[] }>(
        `/referrals/me/buyers?skip=${(page - 1) * PAGE_SIZE}&limit=${PAGE_SIZE}`,
      )
      .then((res) => {
        if (cancelled) return;
        setBuyers(res.data);
        setBuyersTotal(res.total_elements);
      })
      .catch(() => {
        if (!cancelled) toast.error("Failed to load referred buyers");
      })
      .finally(() => {
        if (!cancelled) setBuyersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, toast]);

  async function copyLink() {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy. Please copy the link manually.");
    }
  }

  if (loading) return <Loader className="min-h-[60vh]" />;

  return (
    <PageShell>
      <PageHero
        eyebrow="Buyer Referrals"
        title="Invite buyers, grow your pipeline"
        description="Share your unique referral link with buyers. Anyone who creates an account through it is attributed to you and shown in the table below."
        aside={
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <div className="text-2xl font-extrabold tracking-tight text-foreground">{total}</div>
              <div className="text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                Buyers referred
              </div>
            </div>
          </div>
        }
      />

      {/* Referral link */}
      <PagePanel>
        <PanelHeading
          title="Your referral link"
          description="Copy this link and share it with buyers. It never expires."
        />
        <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center">
          <div className="flex flex-1 items-center gap-2 rounded-lg border border-border/80 bg-muted/30 px-3 py-2.5">
            <Link2 className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              readOnly
              value={referralLink}
              onFocus={(e) => e.currentTarget.select()}
              className="w-full bg-transparent text-sm text-foreground outline-none"
            />
          </div>
          <Button onClick={copyLink} className="shrink-0">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copied" : "Copy link"}
          </Button>
          {typeof navigator !== "undefined" && "share" in navigator && (
            <Button
              variant="outline"
              className="shrink-0 border-primary/20 bg-white/72 hover:bg-white"
              onClick={() =>
                (navigator as Navigator).share?.({
                  title: "Join BusinessLocating",
                  url: referralLink,
                }).catch(() => {})
              }
            >
              <Share2 className="h-4 w-4" />
              Share
            </Button>
          )}
        </div>
        {code && (
          <p className="mt-3 text-xs text-muted-foreground">
            Referral code: <span className="font-mono font-semibold text-foreground">{code}</span>
          </p>
        )}
      </PagePanel>

      {/* Referred buyers */}
      <PagePanel>
        <PanelHeading
          title="Buyers who used your link"
          description={`${total} ${total === 1 ? "buyer has" : "buyers have"} created an account through your referral link.`}
        />
        {buyersLoading && buyers.length === 0 ? (
          <Loader className="min-h-[30vh]" size="sm" />
        ) : buyers.length === 0 ? (
          <EmptyPanel
            icon={Users}
            title="No referred buyers yet"
            description="Share your referral link above. Buyers who sign up through it will appear here."
          />
        ) : (
          <div className="relative overflow-x-auto">
            {buyersLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-card/60 backdrop-blur-[1px]">
                <Loader size="sm" />
              </div>
            )}
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border/80 text-left text-[0.7rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                  <th className="px-3 py-2.5">Buyer</th>
                  <th className="px-3 py-2.5">Email</th>
                  <th className="px-3 py-2.5">Status</th>
                  <th className="px-3 py-2.5">Joined</th>
                </tr>
              </thead>
              <tbody>
                {buyers.map((b) => (
                  <tr key={b.id} className="border-b border-border/50 last:border-0 hover:bg-muted/20">
                    <td className="px-3 py-3 font-semibold text-foreground">
                      {b.first_name} {b.last_name}
                    </td>
                    <td className="px-3 py-3 text-muted-foreground">{b.email}</td>
                    <td className="px-3 py-3">
                      <span
                        className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${
                          STATUS_COLORS[b.status] || "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {STATUS_LABELS[b.status] || b.status}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-muted-foreground">
                      {new Date(b.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              page={page}
              totalElements={buyersTotal}
              pageSize={PAGE_SIZE}
              onChange={setPage}
              className="mt-4"
            />
          </div>
        )}
      </PagePanel>
    </PageShell>
  );
}
